import React, { useState, useEffect } from 'react';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  CheckIcon,
  CalendarIcon,
  DocumentTextIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import PermissionGate, { usePermission } from '../components/PermissionGate';
import NewsNotificationModal from '../components/NewsNotificationModal';

interface NewsItem {
  news_id: number;
  date?: string;
  month?: string;
  news_date: string;
  subject: string;
  sub_category?: string;
  image_url?: string;
  title: string;
  news_text: string;
  quiz_topic?: string;
  extra?: string;
  status?: 'draft' | 'published';
  notification_sent?: number;
  notification_sent_at?: string;
  notification_id?: number;
}

interface NewsListResponse {
  items: NewsItem[];
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export default function NewsManager() {
  const queryClient = useQueryClient();
  const { canDelete, canEdit, canCreate, isAdmin } = usePermission();

  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<NewsItem | null>(null);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [subjectInput, setSubjectInput] = useState('');
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [notificationModal, setNotificationModal] = useState<NewsItem | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    news_date: new Date().toISOString().split('T')[0],
    subject: '',
    sub_category: '',
    title: '',
    news_text: '',
    image_url: '',
    quiz_topic: '',
    extra: '',
    status: 'published' as 'draft' | 'published',
  });

  // Fetch news list
  const { data: newsData, isLoading } = useQuery<NewsListResponse>({
    queryKey: ['news', page, searchQuery, selectedMonth, selectedSubject],
    queryFn: () =>
      api.get('/admin/news/', {
        params: {
          page,
          limit: 20,
          search: searchQuery || undefined,
          month: selectedMonth || undefined,
          subject: selectedSubject || undefined,
        },
      }).then(res => res.data),
  });

  // Fetch unique subjects for autocomplete
  const { data: subjectsData } = useQuery<string[]>({
    queryKey: ['news-subjects'],
    queryFn: () => api.get('/admin/news/subjects').then(res => res.data),
  });

  useEffect(() => {
    if (subjectsData) {
      setSubjects(subjectsData);
    }
  }, [subjectsData]);

  // Create news mutation
  const createNewsMutation = useMutation({
    mutationFn: (data: any) =>
      api.post('/admin/news/', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      queryClient.invalidateQueries({ queryKey: ['news-subjects'] });
      toast.success('News item created successfully');
      setShowAddForm(false);
      resetForm();
    },
    onError: (error: any) => {
      console.error('=== CREATE NEWS ERROR ===');
      console.error('Status:', error.response?.status);
      console.error('Full error:', JSON.stringify(error.response?.data, null, 2));
      console.error('Request data:', JSON.stringify(error.config?.data, null, 2));

      const detail = error.response?.data?.detail;

      // Handle both string and array error formats from FastAPI
      let message = 'Failed to create news item';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        // Format validation errors nicely
        message = detail.map((e: any) => {
          const field = e.loc?.slice(1).join('.') || 'unknown';
          return `${field}: ${e.msg}`;
        }).join('; ');
      }

      toast.error(message, { duration: 5000 });
    },
  });

  // Update news mutation
  const updateNewsMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      api.put(`/admin/news/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      queryClient.invalidateQueries({ queryKey: ['news-subjects'] });
      toast.success('News item updated successfully');
      setEditingItem(null);
      resetForm();
    },
    onError: (error: any) => {
      console.error('=== UPDATE NEWS ERROR ===');
      console.error('Status:', error.response?.status);
      console.error('Full error:', JSON.stringify(error.response?.data, null, 2));
      console.error('Request data:', JSON.stringify(error.config?.data, null, 2));

      const detail = error.response?.data?.detail;

      // Handle both string and array error formats from FastAPI
      let message = 'Failed to update news item';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        // Format validation errors nicely
        message = detail.map((e: any) => {
          const field = e.loc?.slice(1).join('.') || 'unknown';
          return `${field}: ${e.msg}`;
        }).join('; ');
      }

      toast.error(message, { duration: 5000 });
    },
  });

  // Delete news mutation
  const deleteNewsMutation = useMutation({
    mutationFn: (id: number) =>
      api.delete(`/admin/news/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      toast.success('News item deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete news item');
    },
  });

  const resetForm = () => {
    setFormData({
      news_date: new Date().toISOString().split('T')[0],
      subject: '',
      sub_category: '',
      title: '',
      news_text: '',
      image_url: '',
      quiz_topic: '',
      extra: '',
      status: 'published',
    });
    setSubjectInput('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !subjectInput || !formData.news_text) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Build data object with required fields
    const dataToSubmit: any = {
      news_date: formData.news_date,
      subject: subjectInput,
      title: formData.title,
      news_text: formData.news_text,
    };

    // Only add optional fields if they have values
    if (formData.sub_category?.trim()) {
      dataToSubmit.sub_category = formData.sub_category.trim();
    }
    if (formData.image_url?.trim()) {
      dataToSubmit.image_url = formData.image_url.trim();
    }
    if (formData.quiz_topic?.trim()) {
      dataToSubmit.quiz_topic = formData.quiz_topic.trim();
    }
    if (formData.extra?.trim()) {
      dataToSubmit.extra = formData.extra.trim();
    }
    // Only admins choose status; the server coerces staff writes to draft anyway.
    if (isAdmin) {
      dataToSubmit.status = formData.status;
    }

    console.log('=== SUBMITTING NEWS DATA ===');
    console.log(JSON.stringify(dataToSubmit, null, 2));

    if (editingItem) {
      updateNewsMutation.mutate({
        id: editingItem.news_id,
        data: dataToSubmit,
      });
    } else {
      createNewsMutation.mutate(dataToSubmit);
    }
  };

  const handleEdit = (item: NewsItem) => {
    setEditingItem(item);
    setFormData({
      news_date: item.news_date,
      subject: item.subject,
      sub_category: item.sub_category || '',
      title: item.title,
      news_text: item.news_text,
      image_url: item.image_url || '',
      quiz_topic: item.quiz_topic || '',
      extra: item.extra || '',
      status: item.status === 'draft' ? 'draft' : 'published',
    });
    setSubjectInput(item.subject);
    setShowAddForm(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this news item?')) {
      deleteNewsMutation.mutate(id);
    }
  };

  const filteredSubjects = subjects.filter(subject =>
    subject.toLowerCase().startsWith(subjectInput.toLowerCase())
  );

  return (
    <div className="flex flex-col min-h-0">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              News Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Manage daily news articles and updates
            </p>
          </div>
          {canCreate() && (
            <button
              onClick={() => {
                resetForm();
                setEditingItem(null);
                setShowAddForm(!showAddForm);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {showAddForm ? (
                <>
                  <XMarkIcon className="w-5 h-5 mr-2" />
                  Cancel
                </>
              ) : (
                <>
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Add News
                </>
              )}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search news..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => {
              setSelectedMonth(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          <select
            value={selectedSubject}
            onChange={(e) => {
              setSelectedSubject(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">All Subjects</option>
            {subjects.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && canCreate() && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">
            {editingItem ? 'Edit News Item' : 'Add New News Item'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.news_date}
                  onChange={(e) =>
                    setFormData({ ...formData, news_date: e.target.value })
                  }
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={subjectInput}
                  onChange={(e) => {
                    setSubjectInput(e.target.value);
                    setShowSubjectDropdown(true);
                  }}
                  onFocus={() => setShowSubjectDropdown(true)}
                  onBlur={() => setTimeout(() => setShowSubjectDropdown(false), 200)}
                  required
                  placeholder="Type to see suggestions..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                />
                {showSubjectDropdown && filteredSubjects.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredSubjects.map((subject) => (
                      <button
                        key={subject}
                        type="button"
                        onClick={() => {
                          setSubjectInput(subject);
                          setShowSubjectDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        {subject}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sub-category
                </label>
                <input
                  type="text"
                  value={formData.sub_category}
                  onChange={(e) =>
                    setFormData({ ...formData, sub_category: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quiz Topic
                </label>
                <input
                  type="text"
                  value={formData.quiz_topic}
                  onChange={(e) =>
                    setFormData({ ...formData, quiz_topic: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Image URL
              </label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) =>
                  setFormData({ ...formData, image_url: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                News Text <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.news_text}
                onChange={(e) =>
                  setFormData({ ...formData, news_text: e.target.value })
                }
                required
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Extra Information
              </label>
              <textarea
                value={formData.extra}
                onChange={(e) => setFormData({ ...formData, extra: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
              />
            </div>

            {isAdmin ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value as 'draft' | 'published' })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                >
                  <option value="published">Published (visible to users)</option>
                  <option value="draft">Draft (hidden until published)</option>
                </select>
              </div>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                Posts you create save as drafts; an admin will publish.
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingItem(null);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createNewsMutation.isPending || updateNewsMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {(createNewsMutation.isPending || updateNewsMutation.isPending) && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                )}
                {editingItem ? 'Update' : 'Create'} News Item
              </button>
            </div>
          </form>
        </div>
      )}

      {/* News Table */}
      <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm flex flex-col">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : newsData?.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <DocumentTextIcon className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500">No news items found</p>
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {newsData?.items.map((item) => (
                  <tr key={item.news_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {new Date(item.news_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      <div className="flex items-center flex-wrap gap-2">
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {item.subject}
                        </span>
                        {item.sub_category && (
                          <span className="text-xs text-gray-500">
                            ({item.sub_category})
                          </span>
                        )}
                        {item.status === 'draft' && (
                          <span
                            className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                            title="Hidden from end users until an admin publishes"
                          >
                            Draft
                          </span>
                        )}
                        {item.notification_sent === 1 && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            title={`Sent as notification on ${item.notification_sent_at ? new Date(item.notification_sent_at).toLocaleString() : 'unknown date'}`}
                          >
                            <BellIcon className="w-3 h-3" />
                            Sent
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                      <div className="max-w-md truncate">{item.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        {canEdit() && (
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Edit"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                        )}
                        <PermissionGate requireAdmin>
                          <button
                            onClick={() => setNotificationModal(item)}
                            className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                            title="Send as Notification"
                          >
                            <BellIcon className="w-5 h-5" />
                          </button>
                        </PermissionGate>
                        <PermissionGate requireAdmin>
                          <button
                            onClick={() => handleDelete(item.news_id)}
                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            title="Delete"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {newsData && newsData.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {(page - 1) * 20 + 1} to{' '}
                {Math.min(page * 20, newsData.total)} of {newsData.total} results
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <span className="px-3 py-1 text-sm">
                  Page {page} of {newsData.pages}
                </span>
                <button
                  onClick={() => setPage(Math.min(newsData.pages, page + 1))}
                  disabled={page === newsData.pages}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notification Modal */}
      {notificationModal && (
        <NewsNotificationModal
          news={notificationModal}
          onClose={() => setNotificationModal(null)}
          onSuccess={(result) => {
            console.log('Notification sent successfully:', result);
          }}
        />
      )}
    </div>
  );
}